import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchActivityLogs } from '../../api/admin';
import DashboardShell from '../../components/ui/DashboardShell';
import DatePicker from '../../components/ui/DatePicker';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import { ACTION_OPTIONS, activityLabel, describeActivity } from '../../utils/activityLabels';

const ROLES = ['', 'SUPER_ADMIN', 'SALON_MANAGER', 'SALON_STAFF', 'CLIENT'];

const roleBadgeClass = {
  SUPER_ADMIN: 'bg-purple-500/20 text-purple-300',
  SALON_MANAGER: 'bg-glow-600/20 text-glow-300',
  SALON_STAFF: 'bg-blue-500/20 text-blue-300',
  CLIENT: 'bg-zinc-600/20 text-zinc-700 dark:text-zinc-300',
};

const AdminActivityLogsPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [role, setRole] = useState('');
  const [action, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-activity-logs', page, pageSize, role, action, dateFrom, dateTo],
    queryFn: () =>
      fetchActivityLogs({
        page,
        limit: pageSize,
        role: role || undefined,
        action: action || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }),
  });

  return (
    <DashboardShell title={t('admin.activityLogs.title')} subtitle={t('admin.activityLogs.subtitle')}>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <DatePicker
          id="admin-log-from"
          label={t('common.from')}
          compact
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
        />
        <DatePicker
          id="admin-log-to"
          label={t('common.to')}
          compact
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
        >
          {ROLES.map((r) => (
            <option key={r || 'all'} value={r}>
              {r ? t(`roles.${r}`) : t('admin.users.allRoles')}
            </option>
          ))}
        </select>
        <select
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
        >
          <option value="">{t('activity.allActions')}</option>
          {ACTION_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {activityLabel(a)}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 text-zinc-600 dark:text-zinc-500">
            <tr>
              <th className="px-4 py-3">{t('admin.activityLogs.timestamp')}</th>
              <th className="px-4 py-3">{t('admin.activityLogs.actor')}</th>
              <th className="px-4 py-3">{t('admin.activityLogs.action')}</th>
              <th className="px-4 py-3">{t('admin.activityLogs.entity')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-zinc-600 dark:text-zinc-500">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {(data?.data || []).map((log) => (
              <tr key={log.id} className="border-b border-zinc-200/50 dark:border-zinc-800/50">
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500 whitespace-nowrap">
                  {new Date(log.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span>
                      {log.actor.firstName} {log.actor.lastName}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${roleBadgeClass[log.actor.role]}`}>
                      {t(`roles.${log.actor.role}`)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">{describeActivity(log)}</td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">
                  {log.entityType || '—'}
                  {log.entityId ? ` · ${log.entityId.toString().slice(-6)}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginatedFooter
        pagination={data?.pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />
    </DashboardShell>
  );
};

export default AdminActivityLogsPage;
